import type { ErrorEvent } from '@sentry/nextjs';

/**
 * Predicates for the misconfigured-Supabase-keys error classes
 * (BAL-169 / BAL-171). `beforeSend` in the browser and server configs drops
 * both outside production.
 */

/**
 * Detect ZodErrors thrown from getSupabaseClientKeys (the most common
 * symptom of a misconfigured preview without NEXT_PUBLIC_SUPABASE_*).
 */
export function isMissingSupabaseKeysError(
  error: unknown,
  eventErrorValue: string,
): boolean {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : '';
  const combined = `${message}\n${eventErrorValue}`.toLowerCase();
  return (
    combined.includes('next_public_supabase_url') ||
    combined.includes('next_public_supabase_public_key') ||
    combined.includes('next_public_supabase_anon_key')
  );
}

/**
 * Detect the TypeError that getSupabaseClientKeys's OWN catch block used
 * to throw: it assigned `err.message` on a ZodError, and zod v3 defines
 * `ZodError.message` as a prototype getter with no setter, so the
 * assignment itself threw. Earlier versions of this comment blamed
 * Next.js's app-page runtime and a "frozen" ZodError; both were wrong
 * (nothing in `zod/v3/ZodError.js` calls freeze), which BALLEE-J1 proved
 * on 2026-09-04 when the same TypeError took production down behind a
 * missing NEXT_PUBLIC_SUPABASE_ANON_KEY. The catch was fixed in
 * 9d29f582f4 to throw a plain Error naming the variable, so from that
 * commit on this filter can only match events from OLDER deploys.
 * Preview events never reach it (isPreviewEnvironment drops them
 * earlier, BAL-791) and the call site is gated on non-production, so
 * the only residue is a non-production, non-preview, non-localhost host
 * still running a pre-9d29f582f4 build. Kept rather than removed so the
 * DROP_REASONS.supabaseKeysTypeError slug and its coverage test stay
 * stable; removing both is a taxonomy change. Separate Sentry
 * issue (BAL-171 / BALLEE-BV) with no NEXT_PUBLIC_SUPABASE_* substring
 * in the message, so `isMissingSupabaseKeysError` does not catch it. We
 * identify it by matching the symptom message AND requiring
 * `getSupabaseClientKeys` (or its filename) in the stacktrace, to avoid
 * false positives on unrelated getter-only mutations elsewhere.
 */
export function isPreviewSupabaseKeysTypeError(
  event: ErrorEvent,
  eventErrorValue: string,
): boolean {
  const exception = event.exception?.values?.[0];
  if (!exception) return false;
  if (exception.type !== 'TypeError') return false;
  if (!/cannot set property message of/i.test(eventErrorValue)) return false;
  const frames = exception.stacktrace?.frames ?? [];
  return frames.some((frame) => {
    const fn = frame.function ?? '';
    const file = frame.filename ?? '';
    return (
      fn.includes('getSupabaseClientKeys') ||
      file.includes('get-supabase-client-keys')
    );
  });
}
